import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { urlParameterCodes } from '@/constants/urlParams';
import { ParsedAccessoryVariant } from '@/ui/components/pages/ProductDetail/AccessoryDetail/types/ParsedAccessoryVariant';
import { getPathWithoutParams } from '@/ui/utils/client-url-helpers';
import { Accessory } from '@/ui/services/AccessoryService';
import { useCartStore } from '@/ui/store/cart';
import { useCurrentVariant } from './useCurrentVariant';
import { useShallowRouter } from '../useShallowRouter';

// Hook to check if accessory should have a chooser page or if product should be directly added to cart
export function useDirectCartAddition(productData: Accessory) {
  const params = useSearchParams();
  const currentPath = usePathname();
  const { setShowNotifyMe } = useCartStore();

  const { currentVariant } = useCurrentVariant<ParsedAccessoryVariant>(productData);

  const router = useShallowRouter();

  useEffect(() => {
    const hasParam = params.get(urlParameterCodes.productDetail.chooserOpen);

    if (hasParam && !productData.showOverlay) {
      // Resolve variant from SKU param directly to avoid race condition with useCurrentVariant
      const skuParam = params.get(urlParameterCodes.productDetail.sku);
      const variant = (skuParam && productData.getVariantBySKU(skuParam)) || currentVariant;

      // Crystallize can return the product without variants; then there is nothing to add.
      if (variant?.priceVariant) {
        if (productData.isVariantInStock(variant)) {
          productData.addToCart(variant, {});
        } else {
          setShowNotifyMe(true, variant, null);
        }
      }

      // Remove URL params to prevent re-adding the product on refresh or re-navigating.
      const newPath = getPathWithoutParams([
        urlParameterCodes.productDetail.chooserOpen,
        urlParameterCodes.productDetail.sku,
      ]);

      router.replace(newPath);
    }
  }, [params, currentPath]);
}
